import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

// Helper function to get initial theme
function getInitialTheme(): boolean {
  // Check if theme is stored in localStorage
  const storedTheme = localStorage.getItem('theme')
  if (storedTheme) {
    return storedTheme === 'dark'
  }
  
  // Fall back to system preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const useMainStore = defineStore('main', () => {
  // State
  const isDarkMode = ref(getInitialTheme())
  const isLoading = ref(false)
  const contactFormData = ref({
    name: '',
    email: '',
    message: ''
  })

  // Getters
  const theme = computed(() => isDarkMode.value ? 'dark' : 'light')

  // Helper function to apply theme
  function applyTheme() {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme.value)
      localStorage.setItem('theme', theme.value)
    }
  }

  // Actions
  function toggleDarkMode() {
    isDarkMode.value = !isDarkMode.value
    // applyTheme() is called automatically by the watcher
  }

  // Initialize theme on store creation
  function initializeTheme() {
    // Ensure DOM is ready before applying theme
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyTheme)
      } else {
        applyTheme()
      }
    }
  }

  // Watch for theme changes and apply them
  watch(theme, () => {
    applyTheme()
  }, { immediate: false })

  function setLoading(loading: boolean) {
    isLoading.value = loading
  }

  function updateContactForm(data: Partial<typeof contactFormData.value>) {
    contactFormData.value = { ...contactFormData.value, ...data }
  }

  function resetContactForm() {
    contactFormData.value = {
      name: '',
      email: '',
      message: ''
    }
  }

  // Initialize theme immediately
  initializeTheme()

  return {
    // State
    isDarkMode,
    isLoading,
    contactFormData,
    // Getters
    theme,
    // Actions
    toggleDarkMode,
    setLoading,
    updateContactForm,
    resetContactForm,
    initializeTheme
  }
}) 